import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, type AskAnswer } from '../api/client'
import './AskPanel.css'

function day(iso?: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Ask the register a question in words.
 *
 * The answer comes from one of six typed tools over the same stored procedures
 * the screens run — the question is never turned into SQL. That is what makes a
 * figure here and the same figure on the register incapable of disagreeing.
 *
 * Two things about the design are deliberate and worth keeping:
 *
 * **The interpretation is shown above every answer.** A parser that read "next
 * month" as September when October was meant produces a number that looks
 * exactly as confident as a right one. Restating what was understood is what
 * makes that visible before somebody acts on it.
 *
 * **Every counted answer carries the link to the rows behind it.** A number
 * nobody can check is a number nobody should trust — and following the link
 * either proves the figure or shows the question was misread.
 */
export function AskPanel({ onClose }: { onClose: () => void }) {
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState<AskAnswer | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const capabilities = useQuery({
    queryKey: ['ask-capabilities'],
    queryFn: () => api.getAskCapabilities(),
    staleTime: Infinity,
  })

  const ask = useMutation({
    mutationFn: (q: string) => api.ask(q),
    onSuccess: setAnswer,
  })

  // The panel exists to be typed into; landing in it with the cursor elsewhere
  // is a wasted click every time.
  useEffect(() => inputRef.current?.focus(), [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function submit(e: React.FormEvent) {
    e.preventDefault()
    const q = question.trim()
    if (q.length > 0) ask.mutate(q)
  }

  function askExample(example: string) {
    setQuestion(example)
    ask.mutate(example)
  }

  return (
    <div className="askp">
      <div className="askp__head">
        <h2>Ask the register</h2>
        <button type="button" className="askp__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <form className="askp__form" onSubmit={submit}>
        <input
          ref={inputRef}
          type="text"
          value={question}
          maxLength={500}
          placeholder="How many vehicles expire in August 2026?"
          onChange={(e) => setQuestion(e.target.value)}
          aria-label="Your question"
        />
        <button type="submit" className="btn btn--ask" disabled={ask.isPending || !question.trim()}>
          {ask.isPending ? 'Asking…' : 'Ask'}
        </button>
      </form>

      {ask.isError && (
        <p className="askp__error">{(ask.error as Error).message}</p>
      )}

      {answer && <Answer answer={answer} onClose={onClose} />}

      {/*
        The examples are the honest documentation of what this understands.

        Shown until the first answer, and again whenever a question is not
        understood. A box that looks like it takes anything and then declines
        half of what is typed into it teaches people to stop using it.
      */}
      {(!answer || !answer.understood) && capabilities.data && (
        <div className="askp__examples">
          <h3>Try</h3>
          <ul>
            {capabilities.data.examples.map((e) => (
              <li key={e}>
                <button type="button" onClick={() => askExample(e)}>
                  {e}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function Answer({ answer, onClose }: { answer: AskAnswer; onClose: () => void }) {
  return (
    <section className={`askp__answer${answer.understood ? '' : ' askp__answer--unknown'}`}>
      {/* What was understood, above the answer. See the component remarks. */}
      {answer.interpretation && (
        <p className="askp__reading">{answer.interpretation}</p>
      )}

      {answer.figure != null && answer.understood && (
        <p className="askp__figure">{answer.figure.toLocaleString()}</p>
      )}

      <p className="askp__text">{answer.answer}</p>

      {answer.rows && answer.rows.length > 0 && (
        <ul className="askp__rows">
          {answer.rows.map((r, i) => (
            <li key={`${r.registration}-${i}`}>
              <span className="askp__reg mono">{r.registration}</span>
              {r.detail && <span className="askp__detail">{r.detail}</span>}
              {r.expiryDate && <span className="askp__when mono">{day(r.expiryDate)}</span>}
            </li>
          ))}
        </ul>
      )}

      {/* Stated, not buried: a count of obligations is not a count of vehicles,
          and the dashboard has already been bitten by that exact confusion. */}
      {answer.caveat && <p className="askp__caveat">{answer.caveat}</p>}

      {answer.link && (
        <Link className="btn btn--askgo" to={answer.link} onClick={onClose}>
          {answer.understood ? 'See these in the register ›' : 'Open the register ›'}
        </Link>
      )}
    </section>
  )
}
